import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { useAccounts } from '../context/AccountContext';

/**
 * AccountSwitcher - Dropdown to select between connected ad accounts.
 * Only renders if there are 2+ accounts for the given platform.
 *
 * Props:
 *  - platform: 'GOOGLE_ADS' | 'META_ADS'
 *  - onChange: optional callback(accountId) when selection changes
 */
export default function AccountSwitcher({ platform, onChange }) {
  const { fetchAccounts, getAccounts, getSelectedAccountId, selectAccount } = useAccounts();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  const accounts = getAccounts(platform);
  const selectedId = getSelectedAccountId(platform);

  useEffect(() => {
    if (!accounts || accounts.length === 0) {
      fetchAccounts(platform);
    }
  }, [platform]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Don't render if 0 or 1 account
  if (!accounts || accounts.length < 2) return null;

  const handleSelect = (accountId) => {
    selectAccount(platform, accountId);
    setOpen(false);
    if (onChange) onChange(accountId);
  };

  const displayName = (acc) => acc.nickname || acc.accountName || acc.customerId || 'Unknown';

  const selectedLabel = selectedId === 'all'
    ? 'Tüm Hesaplar'
    : displayName(accounts.find(a => a.customerId === selectedId) || {});

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
      >
        <span className="max-w-[200px] truncate">{selectedLabel}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-72 bg-white rounded-xl shadow-lg border border-gray-100 py-1 right-0">
          {/* All Accounts option */}
          <button
            onClick={() => handleSelect('all')}
            className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors ${
              selectedId === 'all' ? 'text-blue-600 bg-blue-50' : 'text-gray-700'
            }`}
          >
            <Check className={`w-4 h-4 ${selectedId === 'all' ? 'opacity-100' : 'opacity-0'}`} />
            <span className="font-medium">Tüm Hesaplar (Birleşik)</span>
          </button>

          <div className="border-t border-gray-100 my-1" />

          {accounts.map((acc) => (
            <button
              key={acc.id}
              onClick={() => handleSelect(acc.customerId)}
              className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors ${
                selectedId === acc.customerId ? 'text-blue-600 bg-blue-50' : 'text-gray-700'
              }`}
            >
              <Check className={`w-4 h-4 flex-shrink-0 ${selectedId === acc.customerId ? 'opacity-100' : 'opacity-0'}`} />
              <div className="flex-1 text-left min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="font-medium truncate">{displayName(acc)}</span>
                </div>
                {acc.customerId && (
                  <div className="text-xs text-gray-400 mt-0.5">{acc.customerId}</div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
